import { formatCurrency } from '@splitfiesta/core';

export interface ReportBarSlice {
  key: string;
  name: string;
  value: number;
}

interface ReportBarListProps {
  slices: ReportBarSlice[];
  currency: string;
}

/**
 * Share of a total as labelled rows with a proportional bar, used by every
 * report drill-down (member, category, month). Every value is text, so the
 * bar colour carries no meaning and stays one brand colour.
 */
export function ReportBarList({ slices, currency }: ReportBarListProps) {
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return null;

  // Preflight strips list styling, which makes Safari drop the list role.
  return (
    <ul className="space-y-4" role="list">
      {slices.map(slice => {
        const share = (slice.value / total) * 100;
        return (
          <li key={slice.key} className="space-y-2">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <span className="min-w-[45%] flex-1 break-words text-sm text-gray-900 dark:text-white sm:text-base">
                {slice.name}
              </span>
              <span className="ml-auto flex flex-wrap items-baseline justify-end gap-x-2">
                <span className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                  {formatCurrency(slice.value, currency)}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                  {share.toFixed(1)}%
                </span>
              </span>
            </div>
            <div
              data-testid={`report-bar-${slice.key}`}
              aria-hidden="true"
              className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
            >
              <div
                data-testid={`report-bar-fill-${slice.key}`}
                className="h-full rounded-full bg-primary-500"
                style={{ width: `${Math.min(100, share)}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
